/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useMemo, useRef } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';
import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';
import {
  CheckboxInput,
  DatePickerInput,
  ReactSelectInput,
  TextInput,
} from '@hivebrite/design-system/Inputs';
import { Label } from '@hivebrite/design-system/Label';
import { usePrevious } from '@hivebrite/react-hooks';

import InputLocation from 'hb-react/frontoffice/components/inputs/InputLocation';
import { ReactSelectIndustriesOrKeyword } from 'hb-react/shared/components/DesignSystem/Inputs/ReactSelect/Async';
import { useQueryOtherInfos } from 'hb-react/shared/hooks/useQueryOtherInfos';

const SearchIcon = () => (
  <FontAwesomeIcon height="16px" iconName="magnifying-glass" />
);

export const locationFilter = {
  id: 'location',
  component: ({ id, value, onChange }) => {
    const componentRef = useRef();

    const prevValue = usePrevious(value);

    useEffect(() => {
      const hasValue = (val) => val?.remote || val?.location;

      if (hasValue(prevValue) && !hasValue(value)) {
        componentRef.current?.resetLocationState();
      }
    }, [prevValue, value]);

    const onlineLabel = s__('Jobs|Remote');

    return (
      <>
        <Label id={id}>{__('Search by location')}</Label>
        <InputLocation
          inputName="address"
          onSelectLocation={onChange}
          placeholder={__('Type and select a location')}
          // We use address here to match the default value of the primaryInputName
          // prop of the InputLocation component.
          defaultLocation={
            value?.remote
              ? { address: onlineLabel }
              : { ...value, address: value?.location }
          }
          id={id}
          componentRef={componentRef}
          onlineEnabled
          onlineLabel={onlineLabel}
          onUpdateOnline={(value) =>
            value ? onChange({ remote: true }) : onChange(null)
          }
        />
      </>
    );
  },
};

export const keywordOrIndustriesFilter = (currentNetwork) => ({
  id: 'industries_or_fulltext',
  component: ({ id, value, onChange }) => (
    <ReactSelectIndustriesOrKeyword
      id={id}
      name={id}
      placeholder={s__(
        'Filters|Type and select one or more %{industry_label} or keyword',
      ).i__({
        industry_label:
          currentNetwork?.user_fields_config?.industries_label?.toLowerCase() ||
          __('industries'),
      })}
      label={__('Search by keyword')}
      onChange={onChange}
      value={value ?? []}
      isMulti
      isSearchable
    />
  ),
});

export const keywordFilter = {
  id: 'industries_or_fulltext',
  component: ({ id, value, onChange }) => (
    <TextInput
      clearable
      icon={SearchIcon}
      id={id}
      name={id}
      label={__('Search by keyword')}
      onChange={(e) => onChange(e.target.value)}
      placeholder={__('Type name or keyword')}
      value={value ?? ''}
    />
  ),
};

export const organizationFilter = {
  id: 'company_name',
  component: ({ id, value, onChange }) => (
    <TextInput
      id={id}
      name={id}
      label={__('Companies')}
      onChange={(event) => onChange(event.target.value)}
      placeholder={__('Type name or keyword')}
      value={value ?? ''}
      icon={SearchIcon}
      clearable
    />
  ),
};

export const remoteOnSiteFiltersHeader = {
  id: 'remote_on_site_header',
  component: () => (
    <>
      <Box as="span" font="h3-regular">
        {__('On-Site/Remote')}
      </Box>
      <Divider mb={2} mt={2} />
    </>
  ),
};

export const onSiteFilter = {
  id: 'on_site',
  component: ({ id, value, onChange }) => (
    <CheckboxInput
      id={id}
      name={id}
      onChange={(event) => onChange(event.target.checked ? 'true' : null)}
      label={__('On-site')}
      ariaLabel={__('On-site')}
      value={value === 'true'}
    />
  ),
};

export const remoteFilter = {
  id: 'remote',
  component: ({ id, value, onChange }) => (
    <CheckboxInput
      id={id}
      name={id}
      onChange={(event) => onChange(event.target.checked ? 'true' : null)}
      label={__('Remote')}
      ariaLabel={__('Remote')}
      value={value === 'true'}
    />
  ),
};

export const hybridFilter = {
  id: 'hybrid',
  component: ({ id, value, onChange }) => (
    <CheckboxInput
      id={id}
      name={id}
      onChange={(event) => onChange(event.target.checked ? 'true' : null)}
      label={__('Hybrid')}
      ariaLabel={__('Hybrid')}
      value={value === 'true'}
    />
  ),
};

export const contractTypeFilter = {
  id: 'contract_type_ids',
  component: ({ id, value, onChange }) => {
    const contractTypes = useQueryOtherInfos({
      getter: (data) => data?.job_contract_types,
    });

    const options = useMemo(
      () =>
        contractTypes.map((type) => ({
          value: type.id.toString(),
          label: I18n.t(type.name),
        })),
      [contractTypes],
    );

    return (
      <ReactSelectInput
        id={id}
        name={id}
        placeholder={__('Select an option')}
        label={__('Type of Contract')}
        options={options}
        onChange={onChange}
        value={value}
        isMulti
        isSearchable={options?.length > 10}
      />
    );
  },
};

export const earliestStartDateFilter = {
  id: 'earliest_start_date',
  component: ({ id, value, onChange }) => (
    <DatePickerInput
      id={id}
      name={id}
      label={__('Earliest Start Date')}
      onChange={onChange}
      value={value}
      displayFormat={I18n.t('date.formats.alt_format')}
      locale={I18n.locale}
    />
  ),
};

export const yearsOfExperienceFilter = {
  id: 'years_of_experience',
  component: ({ id, value, onChange }) => {
    const experienceTypes = useQueryOtherInfos({
      getter: (data) => data?.job_experience_types,
    });

    const options = useMemo(
      () =>
        experienceTypes.map((type) => ({
          value: type.id.toString(),
          label: I18n.t(type.name),
        })),
      [experienceTypes],
    );

    return (
      <ReactSelectInput
        id={id}
        name={id}
        placeholder={__('Select an option')}
        label={__('Years of Experience')}
        options={options}
        onChange={onChange}
        value={value}
        isMulti
        isSearchable={false}
      />
    );
  },
};
